class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      const index = parseInt(this.dataset.index, 10);    
      // Get cart BEFORE update
      const cartStart = await getCartData();    
      const removedItem = cartStart.items[index - 1];      
      if (!removedItem) {
        console.warn('Item not found at index:', index);
        return;
      }
      // Save to global temp
      window.CART_DATA_BLOOM = {
        removedItem,
        event,
        cartStart
      };
    
      window.cartBloom_requestStarted?.({ ...cartStart, removedItem }, event);
  
      await cartItems.updateQuantity(this.dataset.index, 0);

      getCartData().then(cartData => {
        const cartWithRemoved = { ...cartData, removedItem: CART_DATA_BLOOM.removedItem };
        window.cartBloom_requestComplete(cartWithRemoved, event);
        updateCartUI(cartData);
      });
    });

  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }
        const item = parsedState.items.find(i => i.variant_id === parseInt(variantId, 10));
        if (item) {          
          const price = (item.final_line_price / item.quantity) / 100;
          const origin = quantityElement.dataset.origin;
          const objectId = quantityElement.dataset.objectId;
          const queryId = quantityElement.dataset.queryId;
          const indexId = quantityElement.dataset.algoliaIndex;
          const compareAtPrice = parseFloat(quantityElement.dataset.comparePrice) || 0;
          const discount = compareAtPrice > price ? compareAtPrice - price : 0;

          const properties = {
            price: price.toFixed(2),               
            discount_value: discount.toFixed(2),  
            quantity: item.quantity,
            queryId,
            indexId,
            currency: parsedState.currency || 'AED'
          };
          if (origin === 'PLP') {            
            sendAddedToCart(variantId, properties);
          } 
          if (origin === 'Search') {
            sendAfterSearchCart(variantId, properties);            
          }          
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });  
        updateCartUI(parsedState);// cart count and shipping data update
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}



var cartContents = fetch(window.Shopify.routes.root + 'cart.js')
  .then(response => response.json())
  .then(data => { return data });

// cart count and shipping data update use this function for update data
function updateCartUI(cart) {
 
  const countElement = document.querySelector('.cart-page .cart-page-main-cart-items .title-wrapper-with-link .title.title--primary .title-show-count-data');
  const drawercountElement = document.querySelector('cart-drawer .drawer__header .drawer__heading .drawer-title-show-count-data');
  if (countElement) countElement.textContent = `(${cart.item_count})`;
  if (drawercountElement) drawercountElement.textContent = `(${cart.item_count})`;

  const cartIconBubblemobile = document.querySelector('.mobile-header .icon-wrapper .bag .count-holder .cart-count-bubble');
  const cartIconBubblemobilecount = document.querySelector('.mobile-header .icon-wrapper .bag .count-holder .cart-count-bubble .cart-counter');
  if (cartIconBubblemobile && cartIconBubblemobilecount) {
    cartIconBubblemobilecount.textContent = cart.item_count;
    cartIconBubblemobile.classList.toggle('hidden', cart.item_count === 0);
  }
  const freeShippingText = document.querySelectorAll('.free-shipping-indicator__title');
  const freeShippingProgress = document.querySelectorAll('.free-shipping-indicator__progress');
  const ShippingiconProgress = document.querySelectorAll('.free-shipping-indicator__track .icon.icon__delivery');
  if (freeShippingText.length && freeShippingProgress.length && ShippingiconProgress.length) {
    const total = cart.total_price / 100;
    const threshold = 100;
    const remaining = threshold - total;
    freeShippingText.forEach((text, index) => {
      const progress = freeShippingProgress[index];
      const icon = ShippingiconProgress[index];
      if (remaining <= 0) {
        text.textContent = "Congrats! You've qualified for FREE delivery!";
        progress.style.width = '100%';
        icon.style.insetInlineStart = '100%';
      } else {
        text.textContent = `Spend ${remaining.toFixed(2)} AED more to get FREE shipping.`;
        const percent = Math.min((total / threshold) * 100, 100);
        progress.style.width = `${percent}%`;
        icon.style.insetInlineStart = `${percent}%`;
      }
    });
  }
}
function moveToWishlist(el) {
  const wrapper = el.closest('.wislishit-and-remove-button-wrapper');
  if (!wrapper) return;

  const wishlistBtn = wrapper.querySelector('wishlist-button-collection');
  const removeBtn = wrapper.querySelector('cart-remove-button');

  if (wishlistBtn) {
    wishlistBtn.dispatchEvent(new Event('click', { bubbles: true }));
  }

  if (removeBtn) {
    setTimeout(() => {
      removeBtn.click();
    }, 300);
  }
}
